import React, { useState, useEffect, useRef } from 'react';
import { useAuthenticatedFetch } from '../../api/client';
import type { GraphEdge, GraphNode } from '../../api/dastarkhan';
import { usePermissions } from '../../hooks/usePermissions';
import { PropertyEditor } from './PropertyEditor';
import {
  Card,
  Button,
  Modal,
  Stack,
  Flex,
  useTheme,
  spacing,
} from '@fti/design-system';
import { coerceSourceStatus } from '../../utils/sourceStatus';

interface EdgeDetailPanelProps {
  projectId: string;
  edge: GraphEdge | null;
  nodes?: GraphNode[];
  onUpdated: () => void;
  onClose: () => void;
  onEditingChange?: (editing: boolean) => void;
}

function resolveNodeName(nodes: GraphNode[] | undefined, elementId: string): string {
  if (!nodes) return elementId;
  const node = nodes.find((n) => n.element_id === elementId);
  if (!node) return elementId;
  return (node.properties.name as string) || (node.properties.Name as string) || node.labels[0] || elementId;
}

export const EdgeDetailPanel: React.FC<EdgeDetailPanelProps> = ({ projectId, edge, nodes, onUpdated, onClose, onEditingChange }) => {
  const { theme } = useTheme();
  const { canUpdate, canDelete } = usePermissions();
  const { fetchData } = useAuthenticatedFetch();
  const [editing, setEditing] = useState(false);
  const [editProps, setEditProps] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const originalPropsRef = useRef<Record<string, unknown>>({});

  useEffect(() => {
    if (edge) {
      originalPropsRef.current = { ...edge.properties };
      setEditProps({ ...edge.properties });
      setEditing(false);
    }
  }, [edge]);

  if (!edge) return null;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {};
      const original = originalPropsRef.current;

      for (const key of Object.keys(original)) {
        if (!(key in editProps)) {
          payload[key] = null;
        }
      }

      for (const [key, val] of Object.entries(editProps)) {
        if (key === 'source_status') {
          payload[key] = coerceSourceStatus(val as string | null);
        } else {
          payload[key] = val;
        }
      }

      await fetchData(`/dastarkhan/projects/${projectId}/graph/edges/${encodeURIComponent(edge.element_id)}`, {
        method: 'PATCH',
        body: JSON.stringify({ properties: payload }),
      });
      setEditing(false);
      onEditingChange?.(false);
      onUpdated();
    } catch {
      setError('Failed to update edge');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await fetchData(`/dastarkhan/projects/${projectId}/graph/edges/${encodeURIComponent(edge.element_id)}`, {
        method: 'DELETE',
      });
      setShowDeleteConfirm(false);
      onClose();
      onUpdated();
    } catch {
      setError('Failed to delete edge');
    }
  };

  const handleCancel = () => {
    setEditProps({ ...originalPropsRef.current });
    setEditing(false);
    onEditingChange?.(false);
  };

  return (
    <Card>
      <Card.Body>
        <Stack gap="sm">
          <Flex justify="between" align="center">
            <h4 style={{ margin: 0, fontSize: '0.95rem', color: theme.colors.text.primary }}>Edge</h4>
            <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
          </Flex>

          <div>
            <span style={{
              padding: `2px ${spacing.xs}`,
              borderRadius: '4px',
              fontSize: '0.75rem',
              fontWeight: 600,
              background: theme.colors.semantic.warning.light,
              color: theme.colors.semantic.warning.default,
            }}>{edge.type}</span>
          </div>

          <div style={{ fontSize: '0.8rem', color: theme.colors.text.secondary }}>
            <div>From: <span style={{ color: theme.colors.text.primary, fontWeight: 500 }}>{resolveNodeName(nodes, edge.source_element_id)}</span></div>
            <div>To: <span style={{ color: theme.colors.text.primary, fontWeight: 500 }}>{resolveNodeName(nodes, edge.target_element_id)}</span></div>
          </div>

          <div>
            <span style={{ fontSize: '0.75rem', color: theme.colors.text.secondary, fontWeight: 500 }}>Properties</span>
            <div style={{ marginTop: '4px' }}>
              <PropertyEditor
                properties={editing ? editProps : edge.properties}
                onChange={setEditProps}
                readOnly={!editing}
                initialKey={edge.element_id}
              />
            </div>
          </div>

          {error && <div style={{ color: theme.colors.semantic.error.default, fontSize: '0.8rem' }}>{error}</div>}

          <Flex gap="xs">
            {canUpdate(projectId) && !editing && (
              <Button variant="outline" size="sm" onClick={() => { setEditing(true); onEditingChange?.(true); }}>Edit</Button>
            )}
            {editing && (
              <>
                <Button variant="solid" color="primary" size="sm" onClick={handleSave} disabled={saving}>Save</Button>
                <Button variant="outline" size="sm" onClick={handleCancel}>Cancel</Button>
              </>
            )}
            {canDelete(projectId) && !editing && (
              <Button variant="outline" color="error" size="sm" onClick={() => setShowDeleteConfirm(true)}>Delete</Button>
            )}
          </Flex>
        </Stack>
      </Card.Body>

      {showDeleteConfirm && (
        <Modal isOpen onClose={() => setShowDeleteConfirm(false)}>
          <Modal.Header onClose={() => setShowDeleteConfirm(false)}>Confirm Delete</Modal.Header>
          <Modal.Body><p>Delete this edge? This cannot be undone.</p></Modal.Body>
          <Modal.Footer>
            <Flex justify="end" gap="sm">
              <Button variant="outline" size="sm" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
              <Button variant="solid" color="error" size="sm" onClick={handleDelete}>Delete</Button>
            </Flex>
          </Modal.Footer>
        </Modal>
      )}
    </Card>
  );
};
